/**
 * Create the store with dynamic reducers
 */

import { applyMiddleware, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import createReducer from './reducers';
import * as Sentry from '@sentry/browser';
import createSentryMiddleware from 'redux-sentry-middleware';
import { sentryOptions } from './utils/sentryOptions';
import { scrubUrl, scrubValue, scrubString } from './utils/scrub';
import * as env from './env';
import { isSupportedBrowser } from '.';

fetch('/sentryDSN').then(res => res.text()).then(dsn => {
	if(dsn.indexOf('<!doctype html>') === 0) {
		dsn = '';
	}
	Sentry.init({
		dsn,
		environment: env.getEnv(),
		release: env.getVersion(),
		enabled: env.isLive() && isSupportedBrowser,
		// Sessions carry IP/geo data and are not used. IP inference itself is disabled in the
		// Sentry project settings ("Prevent Storing of IP Addresses"): the browser SDK never
		// sends an IP, the server adds it unless that setting is on.
		autoSessionTracking: false,
		ignoreErrors: [
			"502 Bad Gateway",
			"Lock wait timeout exceeded",
			"Non-Error promise rejection captured with value: Timeout",
			'503 Service Temporarily Unavailable',
			'Generator is already running',
			'ChunkLoadError',
			'replaceAll is not a function',
			'Non-Error exception captured',
			'Non-Error promise rejection captured',
			'Failed to fetch',
			'_leaflet_pos'
		],
		beforeSend(event) {
			// filter out UnhandledRejection errors that have no information
			if (event !== undefined && event.exception !== undefined && event.exception.values !== undefined
				&& event?.exception?.values?.length === 1) {
				const e = event.exception.values[0];
				if(e.value.match(/Missing required parameters: sitekey/gms)) return null;
				if(e.value.match(/Loading .* chunk [0-9]* failed.*/gms))
				{
					// Caching issue. New version available.
					window.location.reload();
					return null;
				}
				if (e.type === 'UnhandledRejection' && e.value === 'Non-Error promise rejection captured with value: ') {
					return null;
				}
			}

			// From here on: strip anything that could carry PII or credentials.
			// event.user must stay an allowlist - never forward e-mail/name/ip.
			if (event.user) {
				const {id, accountId, impersonated, impersonatorId} = event.user;
				event.user = {id, accountId, impersonated, impersonatorId};
			}
			if (event.request) {
				if (event.request.url) event.request.url = scrubUrl(event.request.url);
				// Keep User-Agent (browser/OS context); the Referer can carry ids and query data.
				if (event.request.headers) delete event.request.headers.Referer;
				delete event.request.cookies;
			}
			if (event.extra) event.extra = scrubValue(event.extra);
			if (event.contexts) event.contexts = scrubValue(event.contexts);
			if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(b => scrubValue(b));
			if (event.message) event.message = scrubString(event.message);
			if (event.exception?.values) {
				event.exception.values = event.exception.values.map(v => ({
					...v,
					value: scrubString(v.value),
				}));
			}
			return event;
		},
		beforeBreadcrumb(breadcrumb) {
			if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
				if (breadcrumb.data?.url) breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
				if (breadcrumb.data) {
					delete breadcrumb.data.body;
					delete breadcrumb.data.request_body;
					delete breadcrumb.data.response_body;
				}
				return breadcrumb;
			}
			if (breadcrumb.category === 'console') {
				breadcrumb.message = scrubString(breadcrumb.message);
				if (breadcrumb.data) delete breadcrumb.data.arguments;
				return breadcrumb;
			}
			if (typeof breadcrumb.category === 'string' && breadcrumb.category.indexOf('ui.') === 0) {
				// DOM breadcrumbs serialize title/alt/name attributes, which often hold person or device names.
				breadcrumb.message = scrubString(String(breadcrumb.message || '').replace(/\[(type|name|title|alt)="[^"]*"\]/g, '[$1]'));
				return breadcrumb;
			}
			return scrubValue(breadcrumb);
		}
	});
});

const sagaMiddleware = createSagaMiddleware();
const sentryMiddleware = createSentryMiddleware(Sentry, sentryOptions);

let store;

export const getStore = () => (store);

export default function configureStore(initialState = {}) {
	// Create the store with two middlewares
	// 1. sagaMiddleware: Makes redux-sagas work
	// 2. routerMiddleware: Syncs the location/URL path to the state
	const middlewares = [sentryMiddleware, sagaMiddleware];

	const enhancers = [applyMiddleware(...middlewares)];

	// If Redux DevTools Extension is installed use it, otherwise use Redux compose
	/* eslint-disable no-underscore-dangle, indent */
	const composeEnhancers =
		!env.isLive() &&
		typeof window === 'object' &&
		window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
			? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
				// Prevent recomputing reducers for `replaceReducer`
				shouldHotReload: false,
			})
			: compose;
	/* eslint-enable */

	store = createStore(
		createReducer(),
		initialState,
		composeEnhancers(...enhancers),
	);

	const appSaga = require('./redux/sagas/app.saga').default;
	sagaMiddleware.run(appSaga);

	// Extensions
	store.runSaga = sagaMiddleware.run;
	store.injectedReducers = {}; // Reducer registry
	store.injectedSagas = {}; // Saga registry


	// Make reducers hot reloadable, see http://mxs.is/googmo
	/* istanbul ignore next */
	if (module.hot) {
		module.hot.accept('./reducers', () => {
			store.replaceReducer(createReducer(store.injectedReducers));
		});
	}

	return store;
}
